import React, {useEffect, useRef, useState} from 'react';
import {Alert, Box, Button} from '@mui/material';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import {useConfirm} from 'material-ui-confirm';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import AdmicityForm from '../../../../shared-components/AdmicityForm';
import useNotification from '../../../../utility/hooks/useNotification';
import {useGetStaffCategoriesQuery, useManageStaffCategoriesMutation} from '../../../../api/services/lookupsService';
import {
    addRow,
    dragRow,
    isAlphabetical,
    listValidationError,
    moveRow,
    newRowKey,
    normaliseRows,
    parentChoices,
    removalWarning,
    sortAlphabetically,
    subLevelsOf,
    toPayload,
    toRows
} from '../../../../utility/staffCategories';

const BLUE_BUTTON_SX = {
    backgroundColor: '#186BBE',
    boxShadow: 'none',
    width: {xs: '100%', sm: 'auto'},
    '&:hover': {backgroundColor: '#155FA7', boxShadow: 'none'}
};

// AD-2790: the school's own Staff Categories, laid out as Admissions Categories: a name for each, sub levels under a
// category, and the order set with the arrows. There is no default list.
const StaffCategorySettings = () => {
    const {data: categories = [], refetch} = useGetStaffCategoriesQuery();
    const [manageCategories, {isSuccess, isError}] = useManageStaffCategoriesMutation();
    const {showErrorNotification, showSuccessNotification} = useNotification();
    const confirm = useConfirm();
    const [rows, setRows] = useState([]);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [saveValidationError, setSaveValidationError] = useState('');
    const formRef = useRef(null);

    // Maria, 29 October 2026: alphabetical until someone changes the order with the arrows or by dragging
    const [manualOrder, setManualOrder] = useState(false);

    useEffect(() => {
        const loaded = toRows(categories);
        setRows(loaded);
        setManualOrder(!isAlphabetical(loaded));
    }, [categories]);

    const arrange = next => normaliseRows(manualOrder ? next : sortAlphabetically(next));

    useEffect(() => {
        if (isError) {
            showErrorNotification('Failed to update the staff categories');
        } else if (isSuccess) {
            refetch();
            showSuccessNotification('Staff categories updated successfully');
        }
    }, [isSuccess, isError]);

    const subLevelField = {
        label: 'Sub level of',
        name: 'parent',
        inputType: 'select',
        select: true,
        required: false,
        nullableOnEmpty: true,
        options: [{value: '', title: 'None'}, ...parentChoices(rows)]
    };

    const textFields = [
        {
            label: 'Name',
            name: 'name',
            required: true,
            initialValue: selectedRow?.name ?? undefined,
            validate: value => ({
                isValid: Boolean(value && value.trim()) && value.length <= 100,
                message: !value || !value.trim()
                    ? 'Please provide a name, such as Science.'
                    : 'A name cannot exceed 100 characters.'
            })
        },
        // Maria, 29 October 2026: a sub level is chosen when the category is added, so the list can stay alphabetical
        ...(openAddDialog && parentChoices(rows).length > 0 ? [subLevelField] : [])
    ];

    const move = (index, step) => {
        const next = moveRow(rows, index, step);

        if (next !== rows) {
            setManualOrder(true);
            setRows(next);
        }
    };

    const handleDragEnd = result => {
        if (!result.destination || result.destination.index === result.source.index) {
            return;
        }
        setManualOrder(true);
        setRows(dragRow(rows, result.source.index, result.destination.index));
    };

    const setSubLevel = (index, isSubLevel) =>
        setRows(arrange(rows.map((row, i) => i === index ? {...row, isSubLevel} : row)));

    const handleFinishEditing = () => {
        setOpenAddDialog(false);
        setOpenEditDialog(false);
        setSelectedRow(null);
    };

    const handleAdd = payload => {
        setRows(arrange(addRow(rows, {
            id: null,
            key: newRowKey(),
            name: payload.name,
            isSubLevel: false,
            staffCount: 0,
            formCount: 0,
            documentCount: 0
        }, payload.parent)));
        setSaveValidationError('');
        handleFinishEditing();
    };

    const handleEdit = payload => {
        setRows(arrange(rows.map(row => row === selectedRow ? {...row, name: payload.name} : row)));
        setSaveValidationError('');
        handleFinishEditing();
    };

    const handleRemove = index => {
        const row = rows[index];
        const going = [row, ...subLevelsOf(rows, index)];
        const remove = () => setRows(normaliseRows(rows.filter(r => !going.includes(r))));
        const warning = row.id ? removalWarning(row) : null;

        if (!warning) {
            remove();
            return;
        }

        confirm({
            title: `Remove '${row.name}'`,
            description: going.length > 1
                ? `${warning} Its sub levels will be removed with it.`
                : warning,
            confirmationText: 'Remove'
        })
            .then(remove)
            .catch(() => {
            });
    };

    const handleSave = () => {
        const validationError = listValidationError(rows);

        setSaveValidationError(validationError);

        if (!validationError) {
            manageCategories({categories: toPayload(rows)});
        }
    };

    return (
        <Box data-testid="staff-category-settings">
            <Box marginTop={2} marginBottom={2}>
                <Button
                    sx={BLUE_BUTTON_SX}
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon/>}
                    data-testid="staff-category-add"
                    onClick={() => {
                        setSelectedRow(null);
                        setOpenAddDialog(true);
                    }}
                >
                    Add new
                </Button>
            </Box>
            {/* Maria, 29 October 2026: the order can be changed by dragging as well as with the arrows */}
            <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="staff-categories">
            {droppable => (
            <List disablePadding sx={{width: '100%'}} ref={droppable.innerRef} {...droppable.droppableProps}>
                {rows.map((row, index) => (
                    <Draggable key={row.key ?? `staffCategory-${row.id}`} draggableId={row.key ?? `staffCategory-${row.id}`} index={index}>
                    {(draggable, snapshot) => (
                    <ListItem
                        ref={draggable.innerRef}
                        {...draggable.draggableProps}
                        data-testid="staff-category-row"
                        sx={{
                            px: 1,
                            py: 0.25,
                            pl: row.isSubLevel ? 5 : 1,
                            backgroundColor: snapshot.isDragging ? '#e3f5f1' : (index % 2 === 0 ? '#F0FBFA' : '#FFFFFF'),
                            '&:hover': {backgroundColor: '#D4F4F0'}
                        }}
                    >
                        <ListItemText
                            sx={{my: 0}}
                            primary={
                                <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
                                    <Stack flexDirection="row" alignItems="center" gap={1}>
                                        <Box
                                            {...draggable.dragHandleProps}
                                            title="Drag to change the order"
                                            data-testid="staff-category-drag"
                                            sx={{display: 'flex', cursor: 'grab'}}
                                        >
                                            <DragIndicatorIcon fontSize="small" sx={{color: '#9ca3af'}}/>
                                        </Box>
                                        <Typography>{row.name}</Typography>
                                    </Stack>
                                    <Box display="flex">
                                        <IconButton
                                            size="small"
                                            disabled={moveRow(rows, index, -1) === rows}
                                            onClick={() => move(index, -1)}
                                            aria-label="Move category up"
                                        >
                                            <ArrowUpwardOutlinedIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            disabled={moveRow(rows, index, 1) === rows}
                                            onClick={() => move(index, 1)}
                                            aria-label="Move category down"
                                        >
                                            <ArrowDownwardOutlinedIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            disabled={row.isSubLevel || index === 0 || subLevelsOf(rows, index).length > 0}
                                            onClick={() => setSubLevel(index, true)}
                                            aria-label="Make this a sub level of the category above"
                                        >
                                            <FormatIndentIncreaseIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            disabled={!row.isSubLevel}
                                            onClick={() => setSubLevel(index, false)}
                                            aria-label="Make this a category of its own"
                                        >
                                            <FormatIndentDecreaseIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            aria-label={`Rename ${row.name}`}
                                            onClick={() => {
                                                setSelectedRow(row);
                                                setOpenEditDialog(true);
                                            }}
                                        >
                                            <EditIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            aria-label={`Remove ${row.name}`}
                                            onClick={() => handleRemove(index)}
                                        >
                                            <DeleteIcon fontSize="small"/>
                                        </IconButton>
                                    </Box>
                                </Stack>
                            }
                        />
                    </ListItem>
                    )}
                    </Draggable>
                ))}
                {droppable.placeholder}
            </List>
            )}
            </Droppable>
            </DragDropContext>
            <Box display="flex" justifyContent="end" py={2}>
                {saveValidationError
                    ? <Alert severity="error" sx={{width: '100%', mr: {xs: 0, sm: 2}, mb: {xs: 2, sm: 0}}}>
                        {saveValidationError}
                    </Alert>
                    : null}
                <Button sx={BLUE_BUTTON_SX} variant="contained" data-testid="staff-category-save" onClick={handleSave}>
                    Save
                </Button>
            </Box>
            <AdmicityDialog
                handleClose={handleFinishEditing}
                title={openAddDialog ? 'New Staff Category' : 'Update Staff Category'}
                open={openAddDialog || openEditDialog}
                actions={[]}
            >
                <AdmicityForm
                    ref={formRef}
                    textFields={textFields}
                    handleSubmit={data => openAddDialog ? handleAdd(data) : handleEdit(data)}
                    buttonText="Save"
                />
            </AdmicityDialog>
        </Box>
    );
};

export default StaffCategorySettings;
